import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Pencil, RotateCcw, Trash2, Save, Flag, MapPin, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_OPTIONS = {
  disableDefaultUI: false,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: false,
  zoomControl: true,
  clickableIcons: false,
};
const PALETTE = ['#FF0000', '#FB923C', '#FACC15', '#22C55E', '#06B6D4', '#3B82F6', '#A855F7', '#EC4899'];

function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

function totalDistance(points) {
  let d = 0;
  for (let i = 1; i < points.length; i++) d += haversineMeters(points[i - 1], points[i]);
  return d;
}

/**
 * Map-based circuit drawer.
 *
 * Props:
 *   eventId, eventDayId (optional), center {lat,lng}, defaultZoom, onSaved(track),
 *   onCancel(), existingTrack (optional, when editing)
 */
export default function CircuitDrawer({
  eventId, eventDayId, center, defaultZoom = 14, onSaved, onCancel, existingTrack = null,
}) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const [mode, setMode] = useState(existingTrack?.snap_to_roads === false ? 'free' : 'snap');
  const [travelMode, setTravelMode] = useState('WALKING'); // WALKING | BICYCLING | DRIVING
  const [waypoints, setWaypoints] = useState(existingTrack?.snap_to_roads
    ? existingTrack?.waypoints || []  // not stored in legacy; rebuild from points if needed
    : []
  );
  // Computed polyline (snapped or free)
  const [polyline, setPolyline] = useState(existingTrack?.track_points || []);
  const [name, setName] = useState(existingTrack?.name || '');
  const [color, setColor] = useState(existingTrack?.color || PALETTE[0]);
  const [startLabel, setStartLabel] = useState(existingTrack?.start_label || 'Start');
  const [endLabel, setEndLabel] = useState(existingTrack?.end_label || 'Finish');
  const [isCircuit, setIsCircuit] = useState(!!existingTrack?.is_circuit);
  const [saving, setSaving] = useState(false);

  const distance = useMemo(() => totalDistance(polyline), [polyline]);

  // Recompute snapped polyline whenever waypoints change in snap mode.
  useEffect(() => {
    if (mode !== 'snap') return;
    if (waypoints.length < 2 || !window.google?.maps) {
      setPolyline(waypoints);
      return;
    }
    const ds = new window.google.maps.DirectionsService();
    const origin = waypoints[0];
    const destination = waypoints[waypoints.length - 1];
    const intermediates = waypoints.slice(1, -1).map((p) => ({ location: p, stopover: false }));
    ds.route({
      origin, destination, waypoints: intermediates,
      travelMode: window.google.maps.TravelMode[travelMode],
      optimizeWaypoints: false,
    }, (result, status) => {
      if (status === 'OK' && result?.routes?.[0]) {
        const path = result.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
        setPolyline(path);
      } else {
        // Fall back to straight lines if Directions can't route.
        setPolyline(waypoints);
      }
    });
  }, [waypoints, mode, travelMode]);

  // In free mode, polyline === waypoints directly.
  useEffect(() => {
    if (mode === 'free') setPolyline(waypoints);
  }, [waypoints, mode]);

  const onMapClick = (e) => {
    const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setWaypoints((prev) => [...prev, p]);
  };

  const undo = () => setWaypoints((prev) => prev.slice(0, -1));
  const clear = () => { setWaypoints([]); setPolyline([]); };

  const onSave = async () => {
    if (polyline.length < 2) {
      toast.error(t('circuit.noPoints'));
      return;
    }
    if (!name.trim()) {
      toast.error(t('circuit.name'));
      return;
    }
    setSaving(true);
    try {
      const body = {
        event_id: eventId,
        event_day_id: eventDayId || null,
        name: name.trim(),
        description: '',
        color,
        track_points: polyline,
        start_label: startLabel,
        end_label: isCircuit ? startLabel : endLabel,
        is_circuit: isCircuit,
        snap_to_roads: mode === 'snap',
        distance_meters: distance,
        source: mode === 'snap' ? 'directions' : 'manual',
      };
      let res;
      if (existingTrack?.id) {
        res = await axios.put(`${API}/courses/${existingTrack.id}`, body);
      } else {
        res = await axios.post(`${API}/events/${eventId}/courses`, body);
      }
      toast.success(t('circuit.savedSuccess'));
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('auth.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  const start = polyline[0];
  const end = polyline[polyline.length - 1];

  return (
    <div className="absolute inset-0 z-30 bg-white flex flex-col">
      {/* Toolbar */}
      <div className="border-b border-slate-200 bg-white p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <Pencil className="w-4 h-4 text-emerald-600" />
          {t('circuit.drawTitle')}
        </div>

        <div className="flex items-center gap-2 px-2 py-1 rounded bg-slate-100">
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'snap' ? 'bg-white shadow-sm font-semibold' : 'text-slate-600'}`}
            onClick={() => setMode('snap')}
          >{t('circuit.modeSnap')}</button>
          <button
            type="button"
            className={`text-xs px-2 py-1 rounded ${mode === 'free' ? 'bg-white shadow-sm font-semibold' : 'text-slate-600'}`}
            onClick={() => setMode('free')}
          >{t('circuit.modeFree')}</button>
        </div>

        {mode === 'snap' && (
          <Select value={travelMode} onValueChange={setTravelMode}>
            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="WALKING">{t('circuit.modeWalking')}</SelectItem>
              <SelectItem value="BICYCLING">{t('circuit.modeBicycling')}</SelectItem>
              <SelectItem value="DRIVING">{t('circuit.modeDriving')}</SelectItem>
            </SelectContent>
          </Select>
        )}

        <Button size="sm" variant="ghost" onClick={undo} disabled={!waypoints.length}>
          <RotateCcw className="w-3.5 h-3.5 mr-1" /> {t('circuit.undo')}
        </Button>
        <Button size="sm" variant="ghost" onClick={clear} disabled={!waypoints.length}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> {t('circuit.clear')}
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {polyline.length} pts · {(distance / 1000).toFixed(2)} km
          </span>
          <Button size="sm" variant="ghost" onClick={onCancel}>
            <X className="w-3.5 h-3.5 mr-1" /> {t('circuit.cancel')}
          </Button>
          <Button size="sm" onClick={onSave} disabled={saving || polyline.length < 2}>
            <Save className="w-3.5 h-3.5 mr-1" /> {t('circuit.save')}
          </Button>
        </div>
      </div>

      {/* Settings strip */}
      <div className="border-b border-slate-200 bg-slate-50/60 p-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div>
          <Label className="text-xs">{t('circuit.name')}</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('circuit.namePlaceholder')} />
        </div>
        <div>
          <Label className="text-xs">{t('circuit.color')}</Label>
          <div className="flex gap-1 flex-wrap items-center h-9">
            {PALETTE.map((c) => (
              <button key={c} type="button"
                className={`w-6 h-6 rounded-full border-2 ${color === c ? 'border-slate-900' : 'border-white shadow'}`}
                style={{ background: c }}
                onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs">{t('circuit.startLabel')}</Label>
          <Input value={startLabel} onChange={(e) => setStartLabel(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">{t('circuit.endLabel')}</Label>
          <Input value={endLabel} onChange={(e) => setEndLabel(e.target.value)} disabled={isCircuit} />
        </div>
        <div className="flex items-center gap-2 mt-5">
          <Switch checked={isCircuit} onCheckedChange={setIsCircuit} />
          <Label className="text-xs cursor-pointer">{t('circuit.isCircuit')}</Label>
        </div>
      </div>

      {/* Hint */}
      <div className="px-3 py-1.5 bg-emerald-50 border-b border-emerald-100 text-xs text-emerald-900">
        {mode === 'snap' ? t('circuit.instructionsSnap') : t('circuit.instructionsFree')}
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={center || { lat: -33.8688, lng: 151.2093 }}
          zoom={defaultZoom}
          options={MAP_OPTIONS}
          onLoad={(m) => { mapRef.current = m; }}
          onClick={onMapClick}
        >
          {polyline.length >= 2 && (
            <Polyline
              path={polyline}
              options={{ strokeColor: color, strokeWeight: 4, strokeOpacity: 0.9 }}
            />
          )}

          {/* Waypoint dots in snap mode (so user sees the clicked anchors) */}
          {mode === 'snap' && waypoints.map((wp, i) => (
            <Marker key={`wp-${i}`} position={wp}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 5, fillColor: color, fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
              }} />
          ))}

          {start && (
            <Marker position={start} label={{ text: 'S', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 12, fillColor: '#22c55e', fillOpacity: 1, strokeColor: '#15803d', strokeWeight: 2,
              }} title={startLabel} />
          )}
          {end && polyline.length >= 2 && !isCircuit && (
            <Marker position={end} label={{ text: 'F', color: 'white', fontSize: '12px', fontWeight: 'bold' }}
              icon={{
                path: window.google?.maps?.SymbolPath?.CIRCLE,
                scale: 12, fillColor: '#dc2626', fillOpacity: 1, strokeColor: '#7f1d1d', strokeWeight: 2,
              }} title={endLabel} />
          )}
        </GoogleMap>
      </div>
    </div>
  );
}
